import { computed, ref, type Ref } from 'vue';

import { operationLogApi, type OperationLogListParams } from '@/entities/OperationLog';
import { downloadBlobFile, handleApiError, useCustomToast } from '@/shared/lib';

const EXPORT_PROGRESS_TITLE = 'Exportação em andamento';
const EXPORT_PROGRESS_MESSAGE = 'Por favor, aguarde.';
const EXPORT_SUCCESS_TITLE = 'Exportação concluída com sucesso';
const EXPORT_ERROR_TITLE = 'Erro ao exportar';
const EXPORT_ERROR_RETRY_MESSAGE = 'Erro ao exportar. Tente novamente.';

interface UseOperationLogsExportParams {
  getExportParams: () => OperationLogListParams;
  total: Ref<number>;
}

export function useOperationLogsExport({ getExportParams, total }: UseOperationLogsExportParams) {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const isExportDisabled = computed(() => isExporting.value || total.value === 0);

  const handleExportToExcel = async (): Promise<void> => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    const progressToastId = toast.showWarningToast(
      {
        title: EXPORT_PROGRESS_TITLE,
        message: EXPORT_PROGRESS_MESSAGE,
        type: 'warning',
      },
      {
        timeout: false,
        closeButton: false,
      }
    );

    try {
      const { blob, exportedCount, filename } =
        await operationLogApi.exportOperationLogsExcel(getExportParams());

      downloadBlobFile(blob, filename);

      toast.showSuccessToast({
        title: EXPORT_SUCCESS_TITLE,
        message:
          exportedCount === 1 ? '1 registro exportado' : `${exportedCount} registros exportados`,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(error, EXPORT_ERROR_RETRY_MESSAGE);

      toast.showErrorToast({
        title: EXPORT_ERROR_TITLE,
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      toast.dismissToast(progressToastId);
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
    isExportDisabled,
    isExporting,
  };
}
